<template>
  <b-modal id="view-counselor-modal" ref="modal" class="large-modal" size="lg">
    <template #modal-header>
      <div class="upper">
        <label>Counselor Details</label>
        <div v-if="counselor && counselor.user" class="new-title">
          {{ counselor.user.fullName }}
        </div>
      </div>
      <button class="close" @click="hide()">×</button>
    </template>

    <p class="date">
      <span style="margin-bottom: 0">
        Counselor
        <span v-if="counselor && counselor.school">
          - {{ counselor.school.name }}
        </span>
      </span>
    </p>

    <ul v-if="counselor" class="info-list">
      <li v-if="starRating && starRating.averageRating">
        <font-awesome-icon :icon="['fas', 'star']" class="icon alt" />
        {{ starRating.averageRating.score }} / {{ starRating.averageRating.maxScore }}
      </li>
      <li v-if="counselor.office_phone">
        <font-awesome-icon :icon="['fas', 'phone-square']" class="icon alt" />
        <a :href="`tel:${counselor.office_phone}`">
          {{ counselor.office_phone }}
        </a>
      </li>
      <li v-if="counselor.email">
        <font-awesome-icon :icon="['fas', 'envelope']" class="icon alt" />
        <a :href="`mailto:${counselor.email}`">{{ counselor.email }}</a>
      </li>
    </ul>

    <ul v-if="counselor && counselor.statusDisplay" class="pills">
      <li v-if="counselor.isVerified" class="green">
        {{ counselor.statusDisplay }}
      </li>
      <li v-else class="red">{{ counselor.statusDisplay }}</li>
    </ul>

    <hr />

    <div class="row">
      <div v-if="counselor" class="col-12">
        <label>Bio/About</label>
        <p v-if="counselor.biography">{{ counselor.biography }}</p>
        <p v-else>This Counselor hasn't filled out their bio yet.</p>

        <hr />

        <label>Specialties</label>
        <ul v-if="counselor.specialties.length" class="pills">
          <li v-for="s in counselor.specialties" :key="s.display_name">
            {{ s.display_name }}
          </li>
        </ul>
        <p v-else>This counselor did not select any specialties yet</p>

        <hr />

        <label>States Licensed to Practice</label>
        <ul v-if="counselor.registered_in_states.length" class="pills">
          <li v-for="s in counselor.registered_in_states" :key="s.display_name">
            {{ s.display_name }}
          </li>
        </ul>
        <p v-else>This counselor did not select any states yet</p>

        <hr />

        <label>Insurance Accepted</label>
        <div
          v-if="
            counselor.accepted_insurance_carriers &&
            counselor.accepted_insurance_carriers.length
          "
          class="pills"
        >
          <ul>
            <li
              v-for="insurance in counselor.accepted_insurance_carriers"
              :key="insurance.text"
            >
              {{ insurance.text }}
            </li>
          </ul>
        </div>
        <p v-else>This counselor did not select any insurance options yet.</p>

        <hr />
        <tier-picker :counselor="counselor" />

        <hr />
        <paid-as-picker :counselor="counselor" />

        <hr />
        <vacation-picker :counselor-id="counselor.id" />
        
        <hr />
        <accepting-new-clients-picker :counselor-id="counselor.id" />

        <hr />
        <label>Documents</label>
        <counselor-document-table
          v-if="documents.documents && !!documents.documents.length"
          :counselor="counselor"
        />
        <p v-else>This counselor does not have any documents.</p>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button class="btn" type="button" @click="hide()">Dismiss</button>

      <button
        v-if="counselor"
        class="btn"
        :disabled="resetting"
        type="button"
        @click="resetPassword()"
      >
        <font-awesome-icon
          v-if="resetting"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Reset Password</span>
      </button>

      <button
        v-if="counselor && counselor._is_active"
        :disabled="updating"
        class="btn"
        type="button"
        @click="disableUser()"
      >
        <font-awesome-icon
          v-if="updating"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Disable User</span>
      </button>

      <button
        v-if="counselor && !counselor._is_active"
        :disabled="updating"
        class="btn"
        type="button"
        @click="enableUser()"
      >
        <font-awesome-icon
          v-if="updating"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Enable User</span>
      </button>

      <button
        v-if="counselor && counselor.isVerified === null"
        class="btn"
        type="button"
        :disabled="accepting || rejecting"
        @click="reject()"
      >
        <font-awesome-icon
          v-if="rejecting"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Reject</span>
      </button>
      <button
        v-if="counselor && counselor.isVerified === null"
        class="btn btn--primary"
        type="button"
        :disabled="accepting || rejecting"
        @click="accept()"
      >
        <font-awesome-icon
          v-if="accepting"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Accept</span>
      </button>
    </template>
  </b-modal>
</template>

<script>
import AdminClient from '@/api/AdminClient';
import UserClient from '@/api/UserClient';
import CounselorDocumentTable from './CounselorDocumentTable.vue';
import Documents from './documents';
import VacationPicker from '@/bettermynd-admin/accounts/counselor/components/VacationPicker.vue'
import AcceptingNewClientsPicker from '@/bettermynd-admin/accounts/counselor/components/AcceptingNewClientsPicker.vue'
import PaidAsPicker from '@/bettermynd-admin/accounts/counselor/components/PaidAsPicker.vue'
import TierPicker from '@/bettermynd-admin/accounts/counselor/components/TierPicker.vue'

export default {
  name: 'ViewCounselorModal',

  components: {
    CounselorDocumentTable,
    VacationPicker,
    AcceptingNewClientsPicker,
    PaidAsPicker,
    TierPicker
  },

  props: {
    reloadCounselors: {
      type: Function,
      required: true
    }
  },

  provide() {
    return {
      documents: this.documents
    }
  },
  data() {
    return {
      userClient: new UserClient(this.$http),
      adminClient: new AdminClient(this.$http),
      documents: new Documents(),

      counselor: null,
      starRating: null,

      resetting: false,
      updating: false,
      rejecting: false,
      accepting: false,
    };
  },
  methods: {
    async show(counselor) {
      this.counselor = counselor;
      this.accepting = false;
      this.rejecting = false;
      this.resetting = false;
      this.documents.initialize(this.$http, counselor.id);
      this.starRating = await this.adminClient.getCounselorRating(counselor.id)
      
      this.$refs.modal.show();
    },

    hide() {
      this.$refs.modal.hide();
    },

    async resetPassword() {
      this.resetting = true;
      try {
        await this.userClient.forgotPassword(this.counselor.email);
        this.hide();
        this.$store.commit('setGlobalAlert', {
          msg: 'Counselor has been sent a link to reset their password.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to reset password. Please try again!',
          error: true
        });
      }
      this.resetting = false;
    },

    async disableUser() {
      this.updating = true;
      try {
        await this.adminClient.deactivateCounselor(this.counselor.id);
        this.hide();
        this.reloadCounselors();
        this.$store.commit('setGlobalAlert', {
          msg: 'Counselor has been disabled.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to disable counselor. Please try again!',
          error: true
        });
      }
      this.updating = false;
    },

    async enableUser() {
      this.updating = true;
      try {
        await this.adminClient.activateCounselor(this.counselor.id);
        this.hide();
        this.reloadCounselors();
        this.$store.commit('setGlobalAlert', {
          msg: 'Counselor has been enabled.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to enabled counselor. Please try again!',
          error: true
        });
      }
      this.updating = false;
    },

    async reject() {
      this.rejecting = true;
      try {
        await this.adminClient.verifyCounselor(this.counselor.id, false);
        this.hide();
        this.reloadCounselors();
        this.$store.commit('setGlobalAlert', {
          msg: 'Counselor has been rejected.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to reject counselor. Please try again!',
          error: true
        });
      }
      this.rejecting = false;
    },

    async accept() {
      this.accepting = true;
      try {
        await this.adminClient.verifyCounselor(this.counselor.id, true);
        this.hide();
        this.reloadCounselors();
        this.$store.commit('setGlobalAlert', {
          msg: 'Counselor has been accepted.'
        });
      } catch (e) {
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to accept counselor. Please try again!',
          error: true
        });
      }
    },
  }
};
</script>

<style lang="scss" scoped>
#view-counselor-modal {

  .upper {
    display: block;
    position: relative;
    padding-right: 30px;
  }

  label {
    @include label;

    display: block;
    margin-bottom: 10px;
  }

  .date {
    font-size: 18px;
    color: rgba(white, 0.75);
  }

  .pills {
    margin-top: 20px;
  }

  .new-title {
    display: block;
    color: white;
    font-size: 40px;
    font-weight: 700;
    font-family: 'Playfair Display', serif;
    line-height: 1;
  }

  h6 {
    font-weight: normal !important;
    font-size: 25px;
    margin-top: 30px;
    margin-bottom: 10px;

    span {
      font-weight: bold !important;
    }
  }

  .green {
    color: black;
  }

  .info-list {
    list-style-type: none;
    padding-left: 0;
    font-size: 18px;
    margin-bottom: 0;

    i,
    svg {
      color: $green;
      margin-right: 8px;
    }

    li {
      margin-bottom: 5px;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    a {
      color: white;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>
